import { API_URL } from './config';

export async function loginUser(email, password) {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) throw new Error('Login failed');
  return res.json();
}

export async function fetchCurrentUser(token) {
  const res = await fetch(`${API_URL}/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to load current user');
  return res.json();
}

export async function registerUser(email, nickname, password) {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, nickname, password })
  });
  if (!res.ok) throw new Error('Registration failed');
  return res.json();
}

export async function updateMyProfile(token, { email, nickname, profile_picture, password }) {
  const res = await fetch(`${API_URL}/auth/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ email, nickname, profile_picture, password }),
  });

  if (!res.ok) {
    throw new Error('Profile update failed');
  }

  return res.json();
}

// Batch endpoints to reduce API calls
export async function fetchDashboardInitData(token) {
  const res = await fetch(`${API_URL}/init/dashboard`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to load dashboard data');
  return res.json();
}

export async function fetchCatchFormData(token) {
  const res = await fetch(`${API_URL}/init/catch-form`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to load catch form data');
  return res.json();
}

export async function fetchProfileInitData(token, userId) {
  const res = await fetch(`${API_URL}/init/profile/${userId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to load profile data');
  return res.json();
}

export async function fetchLeaderboardInitData(token, groupId) {
  const res = await fetch(`${API_URL}/init/leaderboard/${groupId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to load leaderboard data');
  return res.json();
}

export async function fetchBuddiesInitData(token) {
  const res = await fetch(`${API_URL}/init/buddies`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to load buddies data');
  return res.json();
}

export async function updateCatchVisibility(token, catchVisibility, pbSpeciesSelection) {
  const res = await fetch(`${API_URL}/auth/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      catch_visibility: catchVisibility,
      ...(Array.isArray(pbSpeciesSelection)
        ? { pb_species_selection: pbSpeciesSelection }
        : {}),
    }),
  });

  if (!res.ok) {
    throw new Error('Failed to update catch visibility');
  }

  return res.json();
}

export async function uploadProfilePicture(token, file) {
  const formData = new FormData();
  formData.append('image', file);

  const res = await fetch(`${API_URL}/auth/me/profile-picture`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    throw new Error('Profile picture upload failed');
  }

  return res.json();
}

export async function fetchFishSpecies(token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${API_URL}/fish-species/`, { headers });
  if (!res.ok) throw new Error('Failed to load fish species');
  return res.json();
}

export async function fetchAllFishSpecies(token) {
  const res = await fetch(`${API_URL}/fish-species/manage`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to load species for management');
  return res.json();
}

export async function fetchGroupSpeciesMultipliers(token, groupId) {
  const res = await fetch(`${API_URL}/fish-species/groups/${groupId}/multipliers`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to load group species multipliers');
  return res.json();
}

export async function updateGroupSpeciesMultiplier(token, groupId, speciesId, multiplier) {
  const params = new URLSearchParams({ multiplier: String(multiplier) });
  const res = await fetch(`${API_URL}/fish-species/groups/${groupId}/multipliers/${speciesId}?${params.toString()}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to update group species multiplier');
  return res.json();
}

export async function updateGroupSpeciesMultipliers(token, groupId, items) {
  const res = await fetch(`${API_URL}/fish-species/groups/${groupId}/multipliers`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new Error('Failed to update group species multipliers');
  return res.json();
}

export async function createFishSpecies(token, payload) {
  const res = await fetch(`${API_URL}/fish-species/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to create species');
  return res.json();
}

export async function updateFishSpecies(token, id, payload) {
  const res = await fetch(`${API_URL}/fish-species/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update species');
  return res.json();
}

export async function deleteFishSpecies(token, id) {
  const res = await fetch(`${API_URL}/fish-species/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete species');
}

export async function fetchUserRods(token) {
  const res = await fetch(`${API_URL}/tackle/?type=rod`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to load rods');
  return res.json();
}

export async function fetchUserBaits(token) {
  const res = await fetch(`${API_URL}/tackle/?type=bait`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to load baits');
  return res.json();
}

export async function createUserRod(token, payload) {
  const res = await fetch(`${API_URL}/tackle/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...payload, type: 'rod' }),
  });
  if (!res.ok) throw new Error('Failed to create rod');
  return res.json();
}

export async function createUserBait(token, payload) {
  const res = await fetch(`${API_URL}/tackle/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...payload, type: 'bait' }),
  });
  if (!res.ok) throw new Error('Failed to create bait');
  return res.json();
}

export async function updateUserRod(token, id, payload) {
  const res = await fetch(`${API_URL}/tackle/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update rod');
  return res.json();
}

export async function updateUserBait(token, id, payload) {
  const res = await fetch(`${API_URL}/tackle/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update bait');
  return res.json();
}

export async function deleteUserRod(token, id) {
  const res = await fetch(`${API_URL}/tackle/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete rod');
}

export async function deleteUserBait(token, id) {
  const res = await fetch(`${API_URL}/tackle/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete bait');
}

// ---- Catches API ----
export async function updateCatch(token, id, payload) {
  const res = await fetch(`${API_URL}/catches/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update catch');
  return res.json();
}

export async function deleteCatch(token, id) {
  const res = await fetch(`${API_URL}/catches/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to delete catch');
}

export async function updateCatchImage(token, id, file) {
  const formData = new FormData();
  formData.append('image', file);

  const res = await fetch(`${API_URL}/catches/${id}/image`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to update catch image');
  return res.json();
}

export async function fetchUserCatchStats(token, userId) {
  const res = await fetch(`${API_URL}/catches/users/${userId}/stats`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to load catch stats');
  return res.json();
}

// ---- Buddies API ----
export async function listBuddies(token) {
  const res = await fetch(`${API_URL}/buddies/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load buddies');
  return res.json();
}

export async function listBuddyRequests(token) {
  const res = await fetch(`${API_URL}/buddies/requests`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load requests');
  return res.json();
}

export async function fetchBuddyRelationship(token, otherUserId) {
  const res = await fetch(`${API_URL}/buddies/relationship/${otherUserId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load buddy relationship');
  return res.json();
}

export async function searchUsersForBuddy(token, q) {
  const params = new URLSearchParams({ q });
  const res = await fetch(`${API_URL}/buddies/search?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Search failed');
  return res.json();
}

export async function sendBuddyInvite(token, toUserId) {
  const res = await fetch(`${API_URL}/buddies/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ to_user_id: toUserId }),
  });
  if (!res.ok) throw new Error('Invite failed');
  return res.json();
}

export async function respondBuddyRequest(token, requestId, action) {
  const res = await fetch(`${API_URL}/buddies/requests/${requestId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw new Error('Respond failed');
  return res.json();
}

export async function cancelBuddyRequest(token, requestId) {
  const res = await fetch(`${API_URL}/buddies/requests/${requestId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action: 'cancel' }),
  });
  if (!res.ok) throw new Error('Cancel failed');
  return res.json();
}

export async function removeBuddy(token, buddyUserId) {
  const res = await fetch(`${API_URL}/buddies/${buddyUserId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Remove failed');
  return res.json();
}

export async function fetchBuddiesFeed(token, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await fetch(`${API_URL}/buddies/feed?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load feed');
  return res.json();
}

// Notifications API

export async function fetchNotifications(token, unreadOnly = false, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (unreadOnly) params.append('unread_only', 'true');
  const res = await fetch(`${API_URL}/notifications/?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load notifications');
  return res.json();
}

export async function fetchUnreadCount(token) {
  const res = await fetch(`${API_URL}/notifications/unread-count`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load unread count');
  return res.json();
}

export async function markNotificationRead(token, notificationId) {
  const res = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to mark as read');
  return res.json();
}

export async function markAllNotificationsRead(token) {
  const res = await fetch(`${API_URL}/notifications/read-all`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to mark all as read');
  return res.json();
}

export async function fetchNotificationSettings(token) {
  const res = await fetch(`${API_URL}/notifications/settings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load notification settings');
  return res.json();
}

export async function updateNotificationSettings(token, settings) {
  const res = await fetch(`${API_URL}/notifications/settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error('Failed to update notification settings');
  return res.json();
}

// Push notification API

export async function getVapidPublicKey() {
  const res = await fetch(`${API_URL}/notifications/vapid-public-key`);
  if (!res.ok) throw new Error('Failed to get VAPID public key');
  return res.json();
}

export async function savePushSubscription(token, subscription) {
  const res = await fetch(`${API_URL}/notifications/push-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(subscription),
  });
  if (!res.ok) throw new Error('Failed to save push subscription');
  return res.json();
}

export async function deletePushSubscription(token, endpoint) {
  const res = await fetch(`${API_URL}/notifications/push-subscription?endpoint=${encodeURIComponent(endpoint)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to delete push subscription');
  return res.json();
}

export async function testPushNotification(token) {
  const res = await fetch(`${API_URL}/notifications/test-push`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to send test push');
  return res.json();
}
